import { UPLOAD_MAX_FILES } from '../../api/upload';
import type { ElementComponent } from '../render/elementRenderContext';
import { boolProp, textProp } from '../render/elementProps';
import { FieldFrame, useFieldBox } from './FieldFrame';
import { PhotoPicker, UPLOAD_HINT } from './PhotoPicker';
import { useFormField } from './useFormField';

/**
 * The box a visitor attaches a file to. What it holds is the addresses this shop stored the files
 * under, so a form that submits carries links rather than bytes and nothing has to be uploaded twice.
 *
 * The limits are the shop's own upload route's, not this element's: JPEG, PNG, WebP, GIF and AVIF,
 * 8 MB a file, ten files a field. A merchant cannot widen them from the builder, because a type this
 * accepted and that route refuses is a form that fails at the last step every single time.
 */

/** What a field holds before anything is attached; frozen and shared, so an empty field allocates nothing. */
const NOTHING_ATTACHED: readonly string[] = Object.freeze([]);

// Reads the attached addresses off whatever the form is holding for this field.
const attached = (value: unknown): readonly string[] =>
  Array.isArray(value) ? value.filter((url): url is string => typeof url === 'string') : NOTHING_ATTACHED;

// Draws the attach button, whatever is attached, and the one sentence saying what may be.
export const FileUploadElement: ElementComponent = ({ node }) => {
  const field = useFormField(node, { shape: 'none', empty: NOTHING_ATTACHED });
  const box = useFieldBox(node);
  const many = boolProp(node.props, 'multiple', false);
  return (
    <FieldFrame
      id={field.rule.domId} label={field.rule.label} required={field.rule.required}
      error={field.error} errorId={field.errorId} hint={UPLOAD_HINT} marks={box.marks}
    >
      <PhotoPicker
        id={field.rule.domId}
        buttonLabel={textProp(node.props, 'buttonLabel', 'Choose a file')}
        urls={attached(field.value)}
        onChange={(urls) => field.onChange(urls)}
        limit={many ? UPLOAD_MAX_FILES : 1}
        className={box.className}
        invalid={field.error !== ''}
        describedBy={field.error === '' ? undefined : field.errorId}
      />
    </FieldFrame>
  );
};
